import { useState, useEffect } from 'react';

/** タイプライター表示する日本語フック */
const HERO_HOOK = 'このままで終わるか、ここから変わるか。';

const HOOK_CHARS = [...HERO_HOOK];
const CHAR_MS = 66;
/** タイプ完了後、Reframe までの間（読みのための余白） */
const AFTER_HOOK_MS = 520;
/** Universe 表示後にタイプ開始 */
const TYPING_START_MS = 1550;
/** .hero-fade-in の animation 時間（index.css と一致させる） */
const HERO_FADE_MS = 650;
/** 前の行のフェードが終わってから次へ出すまでの余白 */
const GAP_AFTER_REFRAME = 420;
const GAP_AFTER_TAGLINE = 480;
const GAP_AFTER_CTA = 340;

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function HeroSection() {
  const handleEntryClick = () => {
    const el = document.querySelector('#entry');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const [prefersReduced] = useState(prefersReducedMotion);
  const [typingStarted, setTypingStarted] = useState(false);
  const [line1Count, setLine1Count] = useState(() =>
    prefersReducedMotion() ? HOOK_CHARS.length : 0
  );
  const [showReframe, setShowReframe] = useState(prefersReducedMotion);
  const [showTagline, setShowTagline] = useState(prefersReducedMotion);
  const [showCta, setShowCta] = useState(prefersReducedMotion);
  const [showScroll, setShowScroll] = useState(prefersReducedMotion);

  // タイプライター開始（Universe 表示に合わせて遅延）
  useEffect(() => {
    if (prefersReduced) return;
    const id = window.setTimeout(() => setTypingStarted(true), TYPING_START_MS);
    return () => window.clearTimeout(id);
  }, [prefersReduced]);

  // 一文字ずつ増やす
  useEffect(() => {
    if (!typingStarted || prefersReduced) return;
    if (line1Count >= HOOK_CHARS.length) return;
    const id = window.setTimeout(() => setLine1Count((n) => n + 1), CHAR_MS);
    return () => window.clearTimeout(id);
  }, [typingStarted, line1Count, prefersReduced]);

  // フック入力完了後：1本のタイマー列で Reframe → タグライン → CTA → SCROLL（フェードと重ならない）
  useEffect(() => {
    if (prefersReduced) return;
    if (line1Count < HOOK_CHARS.length) return;

    const tReframe = AFTER_HOOK_MS;
    const tTagline = tReframe + HERO_FADE_MS + GAP_AFTER_REFRAME;
    const tCta = tTagline + HERO_FADE_MS + GAP_AFTER_TAGLINE;
    const tScroll = tCta + HERO_FADE_MS + GAP_AFTER_CTA;

    const idReframe = window.setTimeout(() => setShowReframe(true), tReframe);
    const idTagline = window.setTimeout(() => setShowTagline(true), tTagline);
    const idCta = window.setTimeout(() => setShowCta(true), tCta);
    const idScroll = window.setTimeout(() => setShowScroll(true), tScroll);

    return () => {
      window.clearTimeout(idReframe);
      window.clearTimeout(idTagline);
      window.clearTimeout(idCta);
      window.clearTimeout(idScroll);
    };
  }, [line1Count, prefersReduced]);

  const hookDisplay = HOOK_CHARS.slice(0, line1Count).join('');

  // 光の粒子（スパイラルロゴは変更しない）
  const particles = [
    { top: '12%', left: '15%', size: 7, duration: '4.5s', delay: '0s' },
    { top: '78%', left: '82%', size: 6, duration: '5.5s', delay: '1.3s' },
    { top: '20%', left: '80%', size: 5, duration: '3.8s', delay: '0.6s' },
    { top: '82%', left: '18%', size: 4, duration: '6s', delay: '2.1s' },
    { top: '15%', left: '55%', size: 4, duration: '4.2s', delay: '1.8s' },
    { top: '85%', left: '60%', size: 5, duration: '5s', delay: '0.4s' },
    { top: '8%', left: '38%', size: 3, duration: '3.5s', delay: '1.1s' },
    { top: '30%', left: '92%', size: 3, duration: '7s', delay: '2.5s' },
    { top: '65%', left: '88%', size: 3, duration: '4.8s', delay: '0.9s' },
    { top: '72%', left: '8%', size: 3, duration: '6.2s', delay: '1.6s' },
    { top: '90%', left: '42%', size: 3, duration: '3.9s', delay: '3s' },
    { top: '5%', left: '70%', size: 3, duration: '5.3s', delay: '0.2s' },
  ];

  return (
    <section
      id="hero"
      className="relative flex min-h-[100dvh] w-full items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#0B1A2A' }}
      aria-label="ヒーローセクション"
    >
      {/* スクリーンリーダー用：アニメ後の全文（視覚は順次表示） */}
      <p className="sr-only">
        {HERO_HOOK} Reframe your career. 仕事を通して、人生を切り拓く。
      </p>

      {/* 背景で回るロゴ（クラス・画像は従来どおり／index.css の .hero-spiral-logo） */}
      <img
        src="https://storage.readdy-site.link/project_files/2eebd179-5ab9-4607-b0cd-6c7581f8e0aa/8ac99b23-3fa8-4c29-8a8d-c09dac1c4a68_Universe.png?v=665c240503851196e4e02c00caababa1"
        alt=""
        aria-hidden="true"
        className="hero-spiral-logo"
      />

      {/* 読みやすさ用の極薄ヴィネット（ロゴの上に重なるだけ） */}
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            'radial-gradient(ellipse 85% 65% at 50% 45%, transparent 0%, rgba(11, 26, 42, 0.5) 100%)',
        }}
        aria-hidden={true}
      />

      {particles.map((p, i) => (
        <div
          key={i}
          className="light-particle"
          style={{
            top: p.top,
            left: p.left,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDuration: p.duration,
            animationDelay: p.delay,
            boxShadow:
              p.size >= 6
                ? '0 0 8px 3px rgba(255,255,255,0.7), 0 0 20px 8px rgba(168,205,217,0.35)'
                : p.size >= 4
                  ? '0 0 6px 2px rgba(255,255,255,0.6), 0 0 15px 5px rgba(168,205,217,0.3)'
                  : '0 0 4px 1px rgba(255,255,255,0.5), 0 0 10px 3px rgba(168,205,217,0.25)',
          }}
          aria-hidden="true"
        />
      ))}

      {/* メインコピー：中央の狭いカラム＋上下の装飾ライン */}
      <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-col items-center px-6 text-center sm:px-10 lg:max-w-4xl lg:px-12">
        <div className="hero-text-1 flex w-full max-w-2xl flex-col items-center">
          {/* SEO: ページ主見出し（見た目は従来の p と同一スタイル） */}
          <h1
            className="m-0 font-montserrat font-light leading-[1.1]"
            style={{
              fontSize: 'clamp(36px, 7.5vw, 88px)',
              letterSpacing: '0.28em',
              fontWeight: 300,
              color: 'rgba(168, 205, 217, 0.85)',
            }}
          >
            Universe
          </h1>
        </div>

        <div className="mt-2 flex w-full max-w-md flex-col items-center sm:mt-4">
          <div className="hero-deco-lines mb-8 flex w-full items-center justify-center gap-3">
            <span className="h-px flex-1 max-w-[72px]" style={{ backgroundColor: 'rgba(184, 155, 74, 0.45)' }} />
            <span className="h-1.5 w-1.5 shrink-0 rotate-45" style={{ backgroundColor: '#B89B4A' }} aria-hidden={true} />
            <span className="h-px flex-1 max-w-[72px]" style={{ backgroundColor: 'rgba(184, 155, 74, 0.45)' }} />
          </div>

          <div className="mx-auto flex w-full max-w-xl flex-col items-center px-1 text-center sm:max-w-2xl">
            {/* 話しかけるタイプライター */}
            <p
              className="min-h-[4.25rem] font-light leading-[1.95] tracking-[0.06em] sm:min-h-[3.5rem]"
              style={{
                fontSize: 'clamp(14px, 2.1vw, 19px)',
                fontFamily: "'Noto Serif JP', 'Noto Sans JP', serif",
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.94)',
              }}
              aria-hidden={true}
            >
              {hookDisplay}
              {!prefersReduced && typingStarted && line1Count < HOOK_CHARS.length && (
                <span
                  className="ml-0.5 inline-block h-[1em] w-px animate-pulse bg-white/50"
                  style={{ verticalAlign: '-0.08em' }}
                  aria-hidden={true}
                />
              )}
            </p>

            {showReframe && (
              <p
                className="hero-fade-in mb-2 mt-1 font-montserrat text-[9px] font-light leading-snug tracking-[0.14em] sm:mb-2.5 sm:text-[10px] sm:tracking-[0.16em]"
                style={{ color: 'rgba(255, 210, 151, 0.55)' }}
                aria-hidden={true}
              >
                Reframe your career.
              </p>
            )}

            {showTagline && (
              <p
                className="hero-fade-in font-light leading-[2] tracking-[0.14em]"
                style={{
                  fontSize: 'clamp(13px, 1.85vw, 18px)',
                  fontFamily: "'Noto Serif JP', 'Noto Sans JP', serif",
                  fontWeight: 300,
                  color: 'rgba(255, 255, 255, 0.88)',
                }}
                aria-hidden={true}
              >
                仕事を通して、人生を切り拓く。
              </p>
            )}
          </div>
        </div>

        {showCta && (
          // エントリーボタンと下部 SCROLL インジケーターの間に余白を確保
          <div className="hero-fade-in mt-14 sm:mt-16 mb-10 sm:mb-12">
            <button
              type="button"
              onClick={handleEntryClick}
              className="btn-pill-outline inline-flex min-w-[240px] items-center justify-center border px-10 py-3.5 text-sm font-light tracking-[0.18em] transition-all duration-300 sm:min-w-[280px] sm:px-12"
              style={{
                color: '#B89B4A',
                borderColor: '#B89B4A',
                fontFamily: "'Noto Sans JP', sans-serif",
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                const t = e.currentTarget as HTMLButtonElement;
                t.style.backgroundColor = 'rgba(184, 155, 74, 0.15)';
                t.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                const t = e.currentTarget as HTMLButtonElement;
                t.style.backgroundColor = 'transparent';
                t.style.transform = 'translateY(0)';
              }}
            >
              エントリーはこちら
            </button>
          </div>
        )}
      </div>

      {showScroll && (
        <div
          className="hero-fade-in absolute bottom-12 left-0 right-0 z-[2] mx-auto flex w-fit flex-col items-center gap-2 sm:bottom-16"
          aria-hidden={true}
        >
          <span
            className="font-montserrat text-[10px] font-light tracking-[0.35em] sm:text-xs"
            style={{ color: 'rgba(168, 205, 217, 0.5)' }}
          >
            SCROLL
          </span>
          <div
            className="scroll-line w-px"
            style={{
              height: '36px',
              background: 'linear-gradient(to bottom, rgba(168,205,217,0.45), transparent)',
            }}
          />
        </div>
      )}
    </section>
  );
}
